/**
 * The SSN / tax ID panel (invariant 5).
 *
 * ---------------------------------------------------------------------------
 * Why this is collapsed by default and fetched on demand
 * ---------------------------------------------------------------------------
 *
 * Reading a sensitive record writes a READ entry to `audit_logs`. If this panel
 * loaded with the page, every staff member who opened a tenant to check a phone
 * number would generate an "viewed SSN" entry, and the audit trail would stop
 * meaning anything — the entries that matter would be buried under incidental ones.
 *
 * So the decrypted value is fetched only when someone explicitly asks. The click is
 * the intent, and the intent is what is worth recording.
 *
 * The panel also never keeps the value in a Refine query cache. It is component
 * state, dropped on unmount, because a decrypted SSN sitting in a cache keyed by
 * resource is a decrypted SSN available to any other screen that asks for the same
 * key. See the note on `readSensitive` in lib/dataProvider.ts.
 */

import { useState } from 'react';
import { ApiError } from '../lib/api.js';
import { readSensitive, writeSensitive } from '../lib/dataProvider.js';
import { useCan } from '../lib/capabilities.js';
import { Field, TextArea, TextInput } from './Form.js';

interface TenantSensitive {
  ssn: string | null;
  ssnLast4: string | null;
  dateOfBirth: string | null;
  driversLicense: string | null;
  screeningNotes: string | null;
}

interface OwnerSensitive {
  taxId: string | null;
  taxIdLast4: string | null;
  stripePayoutAccountRef: string | null;
  notes: string | null;
}

type Sensitive = TenantSensitive & OwnerSensitive;

interface SensitivePanelProps {
  resource: 'tenants' | 'owners';
  id: string;
}

export function SensitivePanel({ resource, id }: SensitivePanelProps) {
  const [record, setRecord] = useState<Partial<Sensitive> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  const canRead = useCan('sensitive:read');
  const canWrite = useCan('sensitive:write');

  const isTenant = resource === 'tenants';

  // MAINTENANCE never sees this panel. Three things agree on that and they are not
  // redundant: `tenant_sensitive` has no policy admitting the role, `sensitive:read`
  // refuses the request with a 403, and the panel is not rendered. The last one is
  // the only one that is merely cosmetic — and it is here because a Reveal button
  // that always fails teaches staff to distrust the console.
  if (!canRead) return null;

  async function reveal() {
    setBusy(true);
    setError(null);
    try {
      setRecord(await readSensitive<Sensitive>(resource, id));
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!record) return;

    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      // Only the fields this panel edits are sent. Anything absent is left alone
      // server-side — a partial submit must not wipe an SSN that was not on screen,
      // which is why the API distinguishes "absent" from "null".
      const body = isTenant
        ? {
            ssn: record.ssn ?? null,
            dateOfBirth: record.dateOfBirth ?? null,
            driversLicense: record.driversLicense ?? null,
            screeningNotes: record.screeningNotes ?? null,
          }
        : {
            taxId: record.taxId ?? null,
            stripePayoutAccountRef: record.stripePayoutAccountRef ?? null,
            notes: record.notes ?? null,
          };

      setRecord(await writeSensitive<Sensitive>(resource, id, body));
      setSaved(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  const set =
    <K extends keyof Sensitive>(key: K) =>
    (value: string) =>
      setRecord((previous) => ({ ...previous, [key]: value === '' ? null : value }));

  // An all-null record now means what it says: nothing has been entered. The other
  // reading — "your role cannot see this" — is no longer possible here, because
  // `sensitive:read` answers that case with a 403 before the handler runs. Keeping
  // the two distinguishable matters: an SSN displayed as "nothing on file" is an SSN
  // somebody types in again.
  const looksEmpty =
    record !== null &&
    Object.values(record).every((value) => value === null || value === undefined);

  return (
    <details className="card">
      <summary>
        {isTenant ? 'SSN, date of birth and screening notes' : 'Tax ID and payout reference'}
      </summary>

      <p className="muted">
        Stored encrypted in a separate table. Owners have no access to tenant records at all, and
        the MAINTENANCE role has none to either. Opening this writes an audit entry.
      </p>

      {record === null ? (
        <button type="button" className="button" disabled={busy} onClick={() => void reveal()}>
          {busy ? 'Loading…' : 'Reveal'}
        </button>
      ) : (
        <form className="stack" onSubmit={(event) => void save(event)}>
          {looksEmpty ? <p className="muted">Nothing on file yet.</p> : null}

          {isTenant ? (
            <>
              <Field
                label="SSN"
                name="ssn"
                hint={record.ssnLast4 ? `On file ending ${record.ssnLast4}` : 'Nothing on file'}
              >
                <TextInput name="ssn" value={record.ssn ?? ''} onChange={set('ssn')} />
              </Field>

              <Field label="Date of birth" name="dateOfBirth">
                <TextInput
                  name="dateOfBirth"
                  type="date"
                  value={record.dateOfBirth ?? ''}
                  onChange={set('dateOfBirth')}
                />
              </Field>

              <Field label="Driver's licence" name="driversLicense">
                <TextInput
                  name="driversLicense"
                  value={record.driversLicense ?? ''}
                  onChange={set('driversLicense')}
                />
              </Field>

              <Field label="Screening notes" name="screeningNotes">
                <TextArea
                  name="screeningNotes"
                  value={record.screeningNotes ?? ''}
                  onChange={set('screeningNotes')}
                />
              </Field>
            </>
          ) : (
            <>
              <Field
                label="Tax ID (EIN or SSN)"
                name="taxId"
                hint={record.taxIdLast4 ? `On file ending ${record.taxIdLast4}` : 'Nothing on file'}
              >
                <TextInput name="taxId" value={record.taxId ?? ''} onChange={set('taxId')} />
              </Field>

              <Field
                label="Stripe payout reference"
                name="stripePayoutAccountRef"
                hint="Tokenized reference only. Never a raw account or routing number."
              >
                <TextInput
                  name="stripePayoutAccountRef"
                  value={record.stripePayoutAccountRef ?? ''}
                  onChange={set('stripePayoutAccountRef')}
                />
              </Field>

              <Field label="Notes" name="notes">
                <TextArea name="notes" value={record.notes ?? ''} onChange={set('notes')} />
              </Field>
            </>
          )}

          {error ? (
            <p className="alert alert--error">
              {error instanceof ApiError || error instanceof Error
                ? error.message
                : 'Could not save.'}
            </p>
          ) : null}
          {saved ? <p className="muted">Saved.</p> : null}

          <div className="row">
            {/* Read and write are separate capabilities even though every role that
                has one currently has the other. They are separate in the table so
                that a future read-only auditor role is a one-line change here. */}
            {canWrite ? (
              <button type="submit" className="button" disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            ) : null}
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                // Dropped from memory rather than left on screen. Re-revealing costs
                // one request and one honest audit entry.
                setRecord(null);
                setSaved(false);
                setError(null);
              }}
            >
              Hide
            </button>
          </div>
        </form>
      )}
    </details>
  );
}
